/**
 * Routes Transfer Section Component
 * 
 * Unified split-view interface for transferring routes between Atlas and database.
 * Replaces the previous RoutesDownloadSection with a cleaner, more maintainable design.
 * 
 * Features:
 * - Split-view layout displaying Atlas routes (left) and database routes (right) simultaneously
 * - Bidirectional selection for upload (left → right) and download/delete (right → left) operations
 * - Eliminates code duplication through better component architecture
 * - Reduces visual clutter with modern card-based layout
 * 
 * @module RoutesTransferSection
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Divider,
  Chip,
  LinearProgress,
} from '@mui/material';
import AltRouteOutlined from '@mui/icons-material/AltRouteOutlined';
import { type Environment, getEnvironmentDisplayName } from './EnvironmentSelector';
import { AttachOperationsModal } from '../../fixedRoutes/AttachOperationsModal';
import {
  type UATNetwork,
  type UATNetworkSettingsResponse,
  fetchNetworkSettings,
  fetchOperationsSolutions,
  type UATOperationsSolution,
} from '../../../services/fixedRouteUatClient';
import { AtlasRoutesPanel } from './routes-transfer/AtlasRoutesPanel';
import { DatabaseRoutesPanel } from './routes-transfer/DatabaseRoutesPanel';
import { ActionButtonGroup } from './routes-transfer/ActionButtonGroup';
import { ValidationSummary } from './routes-transfer/ValidationSummary';
import { OperationResults } from './routes-transfer/OperationResults';
import { useUploadRoutes } from '../../../hooks/useUploadRoutes';
import { useDownloadRoutes } from '../../../hooks/useDownloadRoutes';
import { useDeleteRoutes } from '../../../hooks/useDeleteRoutes';
import { OperationConfirmationDialog } from './routes-transfer/OperationConfirmationDialog';
import type { UATRoute } from '../../../services/fixedRouteUatClient';
import { trackAnalyticsEvent } from '../../../services/analytics';
import { useFixedRoutes } from '../../../context/FixedRoutesContext';
import type { FixedRoute } from '../../../types/fixedRoutes';
import { useTranslation } from 'react-i18next';
import type { UploadShiftAssignmentMode } from '../../../services/routeUploadShiftAssignment';

/**
 * Props interface for RoutesTransferSection component
 */
export interface RoutesTransferSectionProps {
  /** JWT token for API authentication */
  jwtToken: string;
  /** Selected environment (UAT or Production) */
  environment: Environment;
  /** Service date for route operations */
  serviceDate: string;
  /** Timezone for route operations */
  timezone: string;
  /** Callback when service date changes */
  onServiceDateChange: (date: string) => void;
  /** Callback when timezone changes */
  onTimezoneChange: (timezone: string) => void;
  /** Available networks from UAT/Production */
  networks: UATNetwork[];
  /** Whether networks are currently loading */
  isLoadingNetworks: boolean;
  /** Error message if network loading failed */
  networkError: string | null;
  /** Selected product ID */
  selectedProductId: string;
  /** Selected network ID */
  selectedNetworkId: number | null;
  /** Callback when product selection changes */
  onProductChange: (productId: string) => void;
  /** Callback to load networks */
  onLoadNetworks: () => void;
  /** Whether download acknowledgment is checked */
  downloadAcknowledged: boolean;
  /** Callback when download acknowledgment changes */
  onDownloadAcknowledgedChange: (acknowledged: boolean) => void;
}

/**
 * RoutesTransferSection Component
 * 
 * Main component for the unified routes transfer interface.
 * Displays Atlas routes and database routes side-by-side in a split-view layout.
 */
export const RoutesTransferSection: React.FC<RoutesTransferSectionProps> = ({
  jwtToken,
  environment,
  serviceDate,
  timezone,
  onServiceDateChange,
  onTimezoneChange,
  networks,
  isLoadingNetworks,
  networkError,
  selectedProductId,
  selectedNetworkId,
  onProductChange,
  onLoadNetworks,
  downloadAcknowledged,
  onDownloadAcknowledgedChange,
}) => {
  const { t } = useTranslation('mapTransfer');
  // Selection state for both panels
  const [atlasSelectedRouteIds, setAtlasSelectedRouteIds] = useState<Set<string>>(new Set());
  const [databaseSelectedRouteIds, setDatabaseSelectedRouteIds] = useState<Set<string>>(new Set());
  
  // Route counts for headers
  const [atlasRouteCount, setAtlasRouteCount] = useState(0);
  const [atlasFilteredCount, setAtlasFilteredCount] = useState<number | undefined>(undefined);
  const [databaseRouteCount, setDatabaseRouteCount] = useState(0);
  const [databaseFilteredCount, setDatabaseFilteredCount] = useState<number | undefined>(undefined);

  // Network settings state (for timezone)
  const [networkSettings, setNetworkSettings] = useState<UATNetworkSettingsResponse | null>(null);
  const [isLoadingNetworkSettings, setIsLoadingNetworkSettings] = useState(false);

  // Get routes from context for confirmation dialogs
  const { routes: atlasRoutes } = useFixedRoutes();

  // "Attach Vehicles & Operations" compose/preview modal — relocated here from
  // ExportImportControls so it lives alongside the route upload controls. It
  // operates on ALL atlas routes from context; `route` is just the anchor
  // for the default service date.
  const [isAttachOpsOpen, setIsAttachOpsOpen] = useState(false);

  // Upload functionality
  const {
    uploadState,
    startUpload,
    validateRoutes: validateUploadRoutes,
    previewUpload,
    resetUpload: resetUploadState,
  } = useUploadRoutes();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [uploadPreview, setUploadPreview] = useState<Awaited<ReturnType<typeof previewUpload>> | null>(null);
  const [uploadPreviewLoading, setUploadPreviewLoading] = useState(false);
  // Defaults to 'warn-only'; handleUpload upgrades this to 'create-dated-shifts'
  // when the current selection carries operationAssignments (see
  // defaultShiftAssignmentMode below). Still user-overridable in the dialog.
  const [shiftAssignmentMode, setShiftAssignmentMode] = useState<UploadShiftAssignmentMode>('warn-only');
  const [operationSolutions, setOperationSolutions] = useState<UATOperationsSolution[]>([]);
  const [operationSolutionsLoading, setOperationSolutionsLoading] = useState(false);
  const [operationSolutionsError, setOperationSolutionsError] = useState<string | null>(null);
  const [selectedOperationSolutionId, setSelectedOperationSolutionId] = useState<string>('');
  const databasePanelRefreshRef = useRef<(() => void) | null>(null);

  // Download functionality
  const { downloadState, startDownload, validateRoutes: validateDownloadRoutes, resetDownload: resetDownloadState } = useDownloadRoutes();
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const atlasPanelRefreshRef = useRef<(() => void) | null>(null);

  // Delete functionality
  const { deleteState, startDelete, validateRoutes: validateDeleteRoutes, resetDelete: resetDeleteState } = useDeleteRoutes();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [databaseRoutes, setDatabaseRoutes] = useState<UATRoute[]>([]);
  const selectedProduct = networks.find((network) => network.productId === selectedProductId);
  const elcStrictMode = /(^|\b)(elc|estee|whitman)(\b|$)/i.test(
    selectedProduct?.productName || '',
  );

  // Reset validation when selection or prerequisites change
  useEffect(() => {
    setShowValidation(false);
    resetUploadState();
  }, [atlasSelectedRouteIds, selectedProductId, selectedNetworkId, serviceDate, resetUploadState]);

  // Reset download state when selection or prerequisites change
  useEffect(() => {
    resetDownloadState();
  }, [databaseSelectedRouteIds, selectedProductId, selectedNetworkId, serviceDate, resetDownloadState]);

  // Do any of the SELECTED Atlas routes carry an assigned plan
  // (`operationAssignments`)? When they do, the upload's effective shift
  // assignment should be driven from that plan ('create-dated-shifts') rather
  // than the legacy 'warn-only' default. We compute this from context routes +
  // the selected ids so the default reflects the current selection.
  //
  // PHASE C: the 'create-dated-shifts' path is still STUBBED/GATED — it returns
  // 501 integrationPending and performs NO live Ops write (see useUploadRoutes:
  // createDatedShiftSolutionForRouteUpload(..., null)). Un-stubbing the real
  // `createshiftsolution` call + UAT QA is Phase C, NOT this change. Auto-
  // selecting the mode here only changes which (already-gated) path runs.
  const selectedRoutesHaveOperationAssignments = atlasRoutes.some(
    (route) =>
      atlasSelectedRouteIds.has(route.id) &&
      !!route.operationAssignments?.length,
  );

  // Effective default for the shift-assignment mode: drive the upload from the
  // assigned plan when any selected route carries operationAssignments, else
  // fall back to 'warn-only'. The user can still override via the dialog's mode
  // control (OperationConfirmationDialog seeds from shiftAssignmentDefaults but
  // owns its own state thereafter).
  const defaultShiftAssignmentMode: UploadShiftAssignmentMode =
    selectedRoutesHaveOperationAssignments ? 'create-dated-shifts' : 'warn-only';

  // Check if upload can be enabled (button clickable, but confirmation required in dialog)
  const canUpload =
    atlasSelectedRouteIds.size > 0 &&
    jwtToken.trim() !== '' &&
    selectedProductId !== '' &&
    selectedNetworkId !== null &&
    downloadAcknowledged &&
    !uploadState.isUploading &&
    !uploadState.isValidating;

  // Check if download can be enabled (button clickable, but confirmation required in dialog)
  const canDownload =
    databaseSelectedRouteIds.size > 0 &&
    jwtToken.trim() !== '' &&
    selectedProductId !== '' &&
    selectedNetworkId !== null &&
    downloadAcknowledged &&
    !downloadState.isDownloading &&
    !downloadState.isValidating &&
    !downloadState.isFetching;

  // Check if delete can be enabled
  const canDelete =
    databaseSelectedRouteIds.size > 0 &&
    jwtToken.trim() !== '' &&
    selectedProductId !== '' &&
    downloadAcknowledged &&
    !deleteState.isDeleting &&
    !deleteState.isValidating;

  // Handle upload button click - open confirmation dialog and kick off the
  // overwrite preview in parallel so the dialog can show "N will be updated,
  // M will be created" without a separate user action.
  const handleUpload = async () => {
    if (!canUpload) {
      return;
    }
    setUploadPreview(null);
    setUploadPreviewLoading(true);
    setUploadDialogOpen(true);
    // Auto-select 'create-dated-shifts' when the selection carries an assigned
    // plan; otherwise default to 'warn-only'. Still overridable in the dialog.
    setShiftAssignmentMode(defaultShiftAssignmentMode);
    setOperationSolutions([]);
    setSelectedOperationSolutionId('');
    setOperationSolutionsError(null);
    try {
      const preview = await previewUpload(atlasSelectedRouteIds, {
        jwtToken,
        productId: selectedProductId,
        environment,
      });
      setUploadPreview(preview);
    } catch (error) {
      console.warn('[RoutesTransferSection] previewUpload failed:', error);
      // Leave preview null — dialog falls back to the generic route list.
    } finally {
      setUploadPreviewLoading(false);
    }

    if (selectedProductId && serviceDate && timezone) {
      setOperationSolutionsLoading(true);
      try {
        const operationsResponse = await fetchOperationsSolutions(
          jwtToken,
          selectedProductId,
          serviceDate,
          timezone,
          environment,
        );
        const entries = operationsResponse.data?.entries || [];
        setOperationSolutions(entries);
        setSelectedOperationSolutionId(entries[0]?.id || '');
      } catch (error) {
        console.warn('[RoutesTransferSection] fetchOperationsSolutions failed:', error);
        setOperationSolutionsError(
          'Unable to load operations solutions. Dated shift creation will remain unavailable.',
        );
      } finally {
        setOperationSolutionsLoading(false);
      }
    }
  };

  // Handle upload confirmation from dialog
  const handleUploadConfirm = async (options?: {
    applyToShifts?: boolean;
    shiftAssignmentMode?: UploadShiftAssignmentMode;
    operationSolutionId?: string;
  }) => {
    // Validate first if not already validated
    let validation = uploadState.validation;
    if (!validation) {
      validation = await validateUploadRoutes(atlasSelectedRouteIds, {
        jwtToken,
        productId: selectedProductId,
        networkId: selectedNetworkId,
        serviceDate,
        timezone,
        downloadAcknowledged,
      });

      if (!validation || !validation.isValid) {
        setShowValidation(true);
        setUploadDialogOpen(false);
        return;
      }
    }

    // Start upload — pass the precomputed preview so we don't re-fetch the
    // route list, and forward the dialog's "apply to shifts" toggle.
    await startUpload(
      atlasSelectedRouteIds,
      {
        jwtToken,
        productId: selectedProductId,
        networkId: selectedNetworkId,
        networkSettings, // Pass network settings to get UUID
        serviceDate,
        timezone,
        downloadAcknowledged,
        environment,
      },
      {
        preview: uploadPreview ?? undefined,
        applyToShifts: options?.applyToShifts ?? true,
        shiftAssignment: {
          mode: options?.shiftAssignmentMode ?? shiftAssignmentMode,
          serviceDate,
          timezone,
          operationSolutionId: options?.operationSolutionId ?? selectedOperationSolutionId,
          operationSolutionName:
            operationSolutions.find(
              (operationSolution) =>
                operationSolution.id ===
                (options?.operationSolutionId ?? selectedOperationSolutionId),
            )?.name ?? undefined,
        },
        routeNameValidation: {
          elcStrictMode,
        },
      },
    );

    // Refresh database panel after upload completes
    // Add a delay to allow the API to propagate the new route
    setTimeout(() => {
      if (databasePanelRefreshRef.current) {
        databasePanelRefreshRef.current();
      }
    }, 1500); // 1.5 second delay to allow API propagation
  };

  // Close upload dialog when upload completes successfully
  useEffect(() => {
    if (uploadDialogOpen && !uploadState.isUploading && uploadState.results.length > 0) {
      // Check if all uploads completed (success or error)
      const allCompleted = Array.from(uploadState.progress.values()).every(
        (p) => p.status === 'success' || p.status === 'error'
      );
      if (allCompleted) {
        setUploadDialogOpen(false);
      }
    }
  }, [uploadDialogOpen, uploadState.isUploading, uploadState.results.length, uploadState.progress]);

  // Close download dialog when download completes successfully
  useEffect(() => {
    if (downloadDialogOpen && !downloadState.isDownloading && !downloadState.isFetching && downloadState.results.length > 0) {
      // Check if all downloads completed (success or error)
      const allCompleted = Array.from(downloadState.progress.values()).every(
        (p) => p.status === 'success' || p.status === 'error'
      );
      if (allCompleted) {
        setDownloadDialogOpen(false);
      }
    }
  }, [downloadDialogOpen, downloadState.isDownloading, downloadState.isFetching, downloadState.results.length, downloadState.progress]);

  // Handle download button click - open confirmation dialog
  const handleDownload = () => {
    if (!canDownload) {
      return;
    }
    setDownloadDialogOpen(true);
  };

  // Handle download confirmation from dialog
  const handleDownloadConfirm = async () => {
    // Validate first if not already validated
    let validation = downloadState.validation;
    if (!validation) {
      validation = await validateDownloadRoutes(databaseSelectedRouteIds, {
        jwtToken,
        productId: selectedProductId,
        networkId: selectedNetworkId,
        serviceDate,
        timezone,
        downloadAcknowledged,
      });

      if (!validation || !validation.isValid) {
        setDownloadDialogOpen(false);
        return;
      }
    }

    // Get selected route names for matching (in case IDs don't match)
    // Normalize names by trimming whitespace
    const selectedRouteNames = databaseRoutes
      .filter((r) => databaseSelectedRouteIds.has(r.id))
      .map((r) => (r.label || r.id).trim());

    // Start download
    await startDownload(databaseSelectedRouteIds, {
      jwtToken,
      productId: selectedProductId,
      networkId: selectedNetworkId,
      serviceDate,
      timezone,
      downloadAcknowledged,
      environment,
      selectedRouteNames, // Pass names for fallback matching
    });

    // Refresh Atlas panel after download completes
    setTimeout(() => {
      if (atlasPanelRefreshRef.current) {
        atlasPanelRefreshRef.current();
      }
    }, 100);
  };

  // Handle delete button click
  const handleDelete = () => {
    if (!canDelete) {
      return;
    }

    // Open confirmation dialog
    setDeleteDialogOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async (deleteUnconnectedStopsFlag: boolean) => {
    // Get selected routes from database routes
    const routesToDelete = databaseRoutes.filter((route) =>
      databaseSelectedRouteIds.has(route.id)
    );

    if (routesToDelete.length === 0) {
      setDeleteDialogOpen(false);
      return;
    }

    // Start delete
    await startDelete(routesToDelete, {
      jwtToken,
      productId: selectedProductId,
      networkId: selectedNetworkId,
      deleteUnconnectedStops: deleteUnconnectedStopsFlag,
      environment,
    });

    // Refresh database panel after delete completes
    setTimeout(() => {
      if (databasePanelRefreshRef.current) {
        databasePanelRefreshRef.current();
      }
      // Clear selection
      setDatabaseSelectedRouteIds(new Set());
      // Close dialog
      setDeleteDialogOpen(false);
    }, 100);
  };

  // Get selected routes for delete dialog
  const selectedRoutesForDelete = databaseRoutes.filter((route) =>
    databaseSelectedRouteIds.has(route.id)
  );

  // Calculate delete progress
  const deleteProgress = deleteState.progress.size > 0
    ? Array.from(deleteState.progress.values()).reduce((sum, p) => sum + (p.progress || 0), 0) /
      deleteState.progress.size
    : 0;

  // Get current route being deleted
  const currentDeleteRoute = Array.from(deleteState.progress.values()).find(
    (p) => p.status === 'deleting'
  )?.routeName;

  // Handle validation button click
  const handleValidate = async () => {
    const validation = await validateUploadRoutes(atlasSelectedRouteIds, {
      jwtToken,
      productId: selectedProductId,
      networkId: selectedNetworkId,
      serviceDate,
      timezone,
      downloadAcknowledged,
    });

    if (validation) {
      setShowValidation(true);
    }
  };

  // Load network settings when product is selected
  useEffect(() => {
    if (
      downloadAcknowledged &&
      jwtToken.trim() &&
      selectedProductId &&
      !isLoadingNetworkSettings
    ) {
      const loadNetworkSettings = async () => {
        setIsLoadingNetworkSettings(true);
        try {
          const response = await fetchNetworkSettings(jwtToken, selectedProductId, environment);
          setNetworkSettings(response);
          
          // Update timezone from network settings if available
          if (response?.data?.networks && response.data.networks.length > 0) {
            const networkSettingsData = response.data.networks[0]?.networkSettings;
            if (networkSettingsData?.timezone && networkSettingsData.timezone !== timezone) {
              onTimezoneChange(networkSettingsData.timezone);
            }
          }
        } catch (error) {
          console.warn('[RoutesTransferSection] Failed to load network settings:', error);
        } finally {
          setIsLoadingNetworkSettings(false);
        }
      };
      loadNetworkSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadAcknowledged, jwtToken, selectedProductId, environment]);

  return (
    <Box
      data-testid="transfer.routes-section.root"
      data-loading={isLoadingNetworkSettings ? 'true' : 'false'}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        overflowX: 'hidden', // Prevent horizontal scrolling
        overflowY: 'auto', // Allow vertical scrolling if needed
        position: 'relative', // Ensure proper stacking context
      }}
    >
      {/* Split-View Container */}
      <Box
        sx={{
          display: 'flex',
          flex: '1 1 0%', // Take available space but allow shrinking
          minHeight: 0, // Allows flex children to shrink below content size
          width: '100%',
          overflowX: 'hidden', // Prevent horizontal scrolling
          overflowY: 'hidden', // Prevent vertical scrolling (panels handle their own)
        }}
      >
        {/* Left Panel: Atlas Routes */}
        <Box
          sx={{
            flex: '1 1 50%',
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0, // Allows flex children to shrink below content size
            backgroundColor: '#f5f7fa', // Light neutral/cool color
            borderRight: '1px solid #e0e0e0', // Divider
          }}
        >
          {/* Panel Header */}
          <Box
            sx={{
              padding: '12px 16px',
              backgroundColor: 'background.paper',
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography variant="h6" component="h2" sx={{ flex: 1, minWidth: 0 }}>
              {t('routes.section.atlasRoutes')}
              {atlasFilteredCount !== undefined
                ? t('routes.section.countWithFilter', { filtered: atlasFilteredCount, total: atlasRouteCount })
                : t('routes.section.countOnly', { total: atlasRouteCount })}
            </Typography>
            {/* Launch the Attach Vehicles & Operations compose/preview modal. */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<AltRouteOutlined fontSize="small" />}
              onClick={() => {
                trackAnalyticsEvent('fixed_route_attach_operations_opened', {
                  environment,
                  network_id: selectedProduct?.networkUuid ?? String(selectedNetworkId ?? ''),
                  route_count: atlasRoutes.length,
                  seeded_auth: Boolean(jwtToken.trim() && selectedProductId),
                  timestamp: new Date().toISOString(),
                });
                setIsAttachOpsOpen(true);
              }}
              disabled={atlasRoutes.length === 0}
              sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              {t('routes.section.attachVehiclesOps', 'Attach Vehicles & Operations')}
            </Button>
          </Box>

          {/* Panel Content - Scrollable */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0, // Critical for flex scrolling
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <AtlasRoutesPanel
              selectedRouteIds={atlasSelectedRouteIds}
              onSelectionChange={setAtlasSelectedRouteIds}
              onRouteCountChange={(count, filteredCount) => {
                setAtlasRouteCount(count);
                setAtlasFilteredCount(filteredCount);
              }}
              onRefreshRegister={(refreshFn) => {
                atlasPanelRefreshRef.current = refreshFn;
              }}
            />
          </Box>

          {/* Upload Progress - Fixed at bottom */}
          {uploadState.isUploading && (
            <Box
              sx={{
                padding: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
                flexShrink: 0,
              }}
            >
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t('routes.section.uploadingRoutes')}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={
                  uploadState.progress.size > 0
                    ? Array.from(uploadState.progress.values()).reduce(
                        (sum, p) => sum + (p.progress || 0),
                        0
                      ) / uploadState.progress.size
                    : 0
                }
                sx={{ mb: 1 }}
              />
              <Typography variant="caption" color="text.secondary">
                {t('routes.section.completedCount', {
                  value: Array.from(uploadState.progress.values()).filter((p) => p.status === 'success').length,
                  total: uploadState.progress.size,
                })}
              </Typography>
            </Box>
          )}

        </Box>

        {/* Divider */}
        <Divider orientation="vertical" flexItem />

        {/* Right Panel: Database Routes */}
        <Box
          sx={{
            flex: '1 1 50%',
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0, // Allows flex children to shrink below content size
            backgroundColor: environment === 'UAT' ? '#f0f4f8' : '#e3f2fd', // Environment-specific color
          }}
        >
          {/* Panel Header */}
          <Box
            sx={{
              padding: '12px 16px',
              backgroundColor: 'background.paper',
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography variant="h6" component="h2">
              {t('routes.section.databaseRoutes')}
              {databaseFilteredCount !== undefined
                ? t('routes.section.countWithFilter', { filtered: databaseFilteredCount, total: databaseRouteCount })
                : t('routes.section.countOnly', { total: databaseRouteCount })}
            </Typography>
            <Chip
              label={getEnvironmentDisplayName(environment)}
              size="small"
              color={environment === 'UAT' ? 'success' : 'primary'}
              sx={{ ml: 1 }}
            />
          </Box>

          {/* Panel Content - Scrollable */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0, // Critical for flex scrolling
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <DatabaseRoutesPanel
              jwtToken={jwtToken}
              environment={environment}
              serviceDate={serviceDate}
              timezone={timezone}
              selectedProductId={selectedProductId}
              selectedNetworkId={selectedNetworkId}
              networkSettings={networkSettings}
              downloadAcknowledged={downloadAcknowledged}
              selectedRouteIds={databaseSelectedRouteIds}
              onSelectionChange={setDatabaseSelectedRouteIds}
              onRouteCountChange={(count, filteredCount) => {
                setDatabaseRouteCount(count);
                setDatabaseFilteredCount(filteredCount);
              }}
              onRefreshRegister={(refreshFn) => {
                databasePanelRefreshRef.current = refreshFn;
              }}
              onRoutesChange={setDatabaseRoutes}
            />
          </Box>

          {/* Download Progress - Fixed at bottom */}
          {downloadState.isDownloading && (
            <Box
              sx={{
                padding: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
                flexShrink: 0,
              }}
            >
              <Typography variant="body2" sx={{ mb: 1 }}>
                {downloadState.isFetching ? t('routes.section.fetchingRoutes') : t('routes.section.downloadingRoutes')}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={
                  downloadState.progress.size > 0
                    ? Array.from(downloadState.progress.values()).reduce(
                        (sum, p) => sum + (p.progress || 0),
                        0
                      ) / downloadState.progress.size
                    : 0
                }
                sx={{ mb: 1 }}
              />
              <Typography variant="caption" color="text.secondary">
                {t('routes.section.completedCount', {
                  value: Array.from(downloadState.progress.values()).filter((p) => p.status === 'success').length,
                  total: downloadState.progress.size,
                })}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Action Buttons - Fixed at bottom, always visible */}
      <Box
        data-testid="transfer.routes-section.action-bar"
        sx={{
          flexShrink: 0,
          flexGrow: 0,
          flexBasis: 'auto',
          borderTop: '2px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          padding: 2,
          zIndex: 10, // Ensure buttons are above other content
          width: '100%',
          boxShadow: '0 -2px 8px rgba(0,0,0,0.1)', // Add shadow to make buttons stand out
        }}
      >
        <ActionButtonGroup
          canUpload={canUpload}
          canDownload={canDownload}
          canDelete={canDelete}
          onUpload={handleUpload}
          onDownload={handleDownload}
          onDelete={handleDelete}
          isProcessing={uploadState.isUploading || uploadState.isValidating || downloadState.isDownloading || downloadState.isValidating || downloadState.isFetching || deleteState.isDeleting || deleteState.isValidating}
          deleteTooltip={
            !databaseSelectedRouteIds.size
              ? t('routes.section.tooltips.deleteNoSelection')
              : !jwtToken.trim()
              ? t('routes.section.tooltips.noJwt')
              : !selectedProductId
              ? t('routes.section.tooltips.noProduct')
              : !downloadAcknowledged
              ? t('routes.section.tooltips.noAck')
              : t('routes.section.tooltips.deleteOk')
          }
          uploadTooltip={
            !atlasSelectedRouteIds.size
              ? t('routes.section.tooltips.uploadNoSelection')
              : !jwtToken.trim()
              ? t('routes.section.tooltips.noJwt')
              : !selectedProductId
              ? t('routes.section.tooltips.noProduct')
              : !selectedNetworkId
              ? t('routes.section.tooltips.noNetwork')
              : !downloadAcknowledged
              ? t('routes.section.tooltips.noAck')
              : t('routes.section.tooltips.uploadOk')
          }
        />
      </Box>

      {/* Validation Summary */}
      {showValidation && uploadState.validation && (
        <Box sx={{ padding: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <ValidationSummary
            validation={{
              isValid: uploadState.validation.isValid,
              errors: uploadState.validation.errors,
              warnings: uploadState.validation.warnings,
              routeIssues: uploadState.validation.routeIssues,
            }}
            defaultExpanded={!uploadState.validation.isValid}
          />
        </Box>
      )}


      {/* Upload Results - Scrollable section */}
      {uploadState.results.length > 0 && (
        <Box
          sx={{
            padding: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            flexShrink: 0,
            maxHeight: '300px',
            overflow: 'auto',
          }}
        >
          <OperationResults
            operationType="upload"
            results={uploadState.results.map((r) => ({
              routeId: r.routeId,
              routeName: r.routeName,
              success: r.success,
              error: r.error,
              createdRouteVersionId: r.createdRouteVersionId,
              routeVersionName: r.routeVersionName,
              uatRouteId: r.uatRouteId,
              wasUpdate: r.wasUpdate,
              shiftUpdateApplied: r.shiftUpdateApplied,
              shiftUpdateError: r.shiftUpdateError,
              datedShiftAttempted: r.datedShiftAttempted,
              datedShiftCreated: r.datedShiftCreated,
              datedShiftSolutionId: r.datedShiftSolutionId,
              datedShiftWarning: r.datedShiftWarning,
              datedShiftError: r.datedShiftError,
              warnings: r.warnings,
            }))}
            dismissible
            onDismiss={resetUploadState}
          />
        </Box>
      )}

      {/* Download Results - Scrollable section */}
      {downloadState.results.length > 0 && (
        <Box
          sx={{
            padding: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            flexShrink: 0,
            maxHeight: '300px',
            overflow: 'auto',
          }}
        >
          <OperationResults
            operationType="download"
            results={downloadState.results.map((r) => ({
              routeId: r.routeId,
              routeName: r.routeName,
              success: r.success,
              error: r.error,
              info: r.info,
            }))}
            dismissible
            onDismiss={resetDownloadState}
          />
        </Box>
      )}

      {/* Delete Results - Scrollable section */}
      {deleteState.results.length > 0 && (
        <Box
          sx={{
            padding: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            flexShrink: 0,
            maxHeight: '300px',
            overflow: 'auto',
          }}
        >
          <OperationResults
            operationType="delete"
            results={deleteState.results.map((r) => ({
              routeId: r.routeId,
              routeName: r.routeName,
              success: r.success,
              error: r.error,
            }))}
            dismissible
            onDismiss={resetDeleteState}
          />
        </Box>
      )}

      {/* Upload Confirmation Dialog */}
      <OperationConfirmationDialog
        operationType="upload"
        open={uploadDialogOpen}
        onClose={() => {
          if (!uploadState.isUploading) {
            setUploadDialogOpen(false);
            setUploadPreview(null);
          }
        }}
        routes={atlasRoutes.filter((r) => atlasSelectedRouteIds.has(r.id))}
        environment={environment}
        isProcessing={uploadState.isUploading}
        progress={
          uploadState.progress.size > 0
            ? (Array.from(uploadState.progress.values()).filter((p) => p.status === 'success').length /
                uploadState.progress.size) *
              100
            : 0
        }
        currentRoute={
          Array.from(uploadState.progress.values()).find((p) => p.status === 'uploading')?.routeName
        }
        onConfirm={handleUploadConfirm}
        uploadPreview={uploadPreview}
        uploadPreviewLoading={uploadPreviewLoading}
        operationSolutions={operationSolutions.map((operationSolution) => ({
          id: operationSolution.id,
          name: operationSolution.name,
          startDate: operationSolution.startDate,
          endDate: operationSolution.endDate,
        }))}
        operationSolutionsLoading={operationSolutionsLoading}
        operationSolutionsError={operationSolutionsError}
        shiftAssignmentDefaults={{
          mode: shiftAssignmentMode,
          operationSolutionId: selectedOperationSolutionId,
        }}
      />

      {/* Download Confirmation Dialog */}
      <OperationConfirmationDialog
        operationType="download"
        open={downloadDialogOpen}
        onClose={() => {
          if (!downloadState.isDownloading && !downloadState.isFetching) {
            setDownloadDialogOpen(false);
          }
        }}
        routes={databaseRoutes.filter((r) => databaseSelectedRouteIds.has(r.id))}
        environment={environment}
        isProcessing={downloadState.isDownloading || downloadState.isFetching}
        progress={
          downloadState.progress.size > 0
            ? (Array.from(downloadState.progress.values()).filter((p) => p.status === 'success').length /
                downloadState.progress.size) *
              100
            : 0
        }
        currentRoute={
          Array.from(downloadState.progress.values()).find((p) => p.status === 'fetching' || p.status === 'importing')
            ?.routeName
        }
        onConfirm={handleDownloadConfirm}
      />

      {/* Delete Confirmation Dialog */}
      <OperationConfirmationDialog
        operationType="delete"
        open={deleteDialogOpen}
        onClose={() => {
          if (!deleteState.isDeleting) {
            setDeleteDialogOpen(false);
          }
        }}
        routes={selectedRoutesForDelete}
        environment={environment}
        isProcessing={deleteState.isDeleting}
        progress={deleteProgress}
        currentRoute={currentDeleteRoute}
        onConfirm={(options) => handleDeleteConfirm(options?.deleteUnconnectedStops || false)}
      />

      {/* Attach Vehicles & Operations (compose/preview draft) modal — seeded with
          the drawer's JWT / environment / networks / product so vehicles and
          operations auto-load without re-entry. Operates on all routes; the first
          route is just the default service-date anchor. */}
      <AttachOperationsModal
        open={isAttachOpsOpen}
        onClose={() => setIsAttachOpsOpen(false)}
        route={atlasRoutes[0] ?? null}
        seedJwtToken={jwtToken}
        seedEnvironment={environment}
        seedNetworks={networks}
        seedProductId={selectedProductId}
      />
    </Box>
  );
};
